import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

// Layouts
import DashboardLayout from '@/app/layouts/DashboardLayout';
import AuthLayout from '@/app/layouts/AuthLayout';

// Auth Pages
import LoginPage from '@/app/routes/auth/LoginPage';

// Dashboard
import DashboardPage from '@/app/routes/dashboard/DashboardPage';

// TTS Pages
import TTSUploadPage from '@/app/routes/tts/upload/TTSUploadPage';
import TTSJobsPage from '@/app/routes/tts/jobs/TTSJobsPage';
import TTSJobDetailPage from '@/app/routes/tts/jobs/TTSJobDetailPage';
import TTSJobMonitorPage from '@/app/routes/tts/jobs/TTSJobMonitorPage';
import TTSPlayerPage from '@/app/routes/tts/player/TTSPlayerPage';
import TTSTelemetryPage from '@/app/routes/tts/telemetry/TTSTelemetryPage';
import TTSTelemetryRunPage from '@/app/routes/tts/telemetry/TTSTelemetryRunPage';
import TTSTelemetryComparePage from '@/app/routes/tts/telemetry/TTSTelemetryComparePage';
import TTSHealthPage from '@/app/routes/tts/health/TTSHealthPage';

// Distribution Pages
import DistBooksPage from '@/app/routes/distribution/books/DistBooksPage';
import DistBookDetailPage from '@/app/routes/distribution/books/DistBookDetailPage';
import DistPublishersPage from '@/app/routes/distribution/publishers/DistPublishersPage';
import DistPublisherDetailPage from '@/app/routes/distribution/publishers/DistPublisherDetailPage';
import DistPlatformsPage from '@/app/routes/distribution/platforms/DistPlatformsPage';
import DistPlatformDetailPage from '@/app/routes/distribution/platforms/DistPlatformDetailPage';
import DistFeedsPage from '@/app/routes/distribution/feeds/DistFeedsPage';
import DistDeliveriesPage from '@/app/routes/distribution/deliveries/DistDeliveriesPage';
import DistDeliveryDetailPage from '@/app/routes/distribution/deliveries/DistDeliveryDetailPage';
import DistSalesPage from '@/app/routes/distribution/sales/DistSalesPage';

// Admin Pages
import AdminOrganizationsPage from '@/app/routes/admin/AdminOrganizationsPage';
import AdminUsersPage from '@/app/routes/admin/AdminUsersPage';
import AdminSettingsPage from '@/app/routes/admin/AdminSettingsPage';

// Error Pages
import NotFoundPage from '@/app/routes/NotFoundPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuthStore();
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* TTS Pipeline */}
        <Route path="/tts/upload" element={<TTSUploadPage />} />
        <Route path="/tts/jobs" element={<TTSJobsPage />} />
        <Route path="/tts/jobs/:jobId" element={<TTSJobDetailPage />} />
        <Route path="/tts/jobs/:jobId/monitor" element={<TTSJobMonitorPage />} />
        <Route path="/tts/player/:jobId" element={<TTSPlayerPage />} />
        <Route path="/tts/telemetry" element={<TTSTelemetryPage />} />
        <Route path="/tts/telemetry/compare" element={<TTSTelemetryComparePage />} />
        <Route path="/tts/telemetry/:runId" element={<TTSTelemetryRunPage />} />
        <Route path="/tts/health" element={<TTSHealthPage />} />

        {/* Distribution Pipeline */}
        <Route path="/distribution/books" element={<DistBooksPage />} />
        <Route path="/distribution/books/:id" element={<DistBookDetailPage />} />
        <Route path="/distribution/publishers" element={<DistPublishersPage />} />
        <Route path="/distribution/publishers/:id" element={<DistPublisherDetailPage />} />
        <Route path="/distribution/platforms" element={<DistPlatformsPage />} />
        <Route path="/distribution/platforms/:id" element={<DistPlatformDetailPage />} />
        <Route path="/distribution/feeds" element={<DistFeedsPage />} />
        <Route path="/distribution/deliveries" element={<DistDeliveriesPage />} />
        <Route path="/distribution/deliveries/:id" element={<DistDeliveryDetailPage />} />
        <Route path="/distribution/sales" element={<DistSalesPage />} />

        {/* Admin */}
        <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;

